import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { createOfficeService, updateOfficeService, fetchOfficeServiceById } from "./supportContactService";
import "../assets/css/AddSupportContact.css";

/**
 * AddOfficeService
 *
 * Form to create a new office service.
 * Integrates with POST /create-office-service API.
 */
function AddOfficeService({ onBack, onAddService }) {
  const navigate = useNavigate();
  const { id: serviceId } = useParams();
  const isEdit = Boolean(serviceId);
  const [loading, setLoading] = useState(isEdit);
  const [formData, setFormData] = useState({
    serviceName: "",
    serviceCategory: "",
    contactName: "",
    contactPhone: "",
    altPhone: "",
    supportEmail: "",
    supportHours: "",
    supportDays: "",
    notes: "",
    status: "Active",
    tollFreeNumber: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const getUserId = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.user_id ?? user.id ?? 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    return 1;
  };

  useEffect(() => {
    if (!isEdit || !serviceId) return;
    const load = async () => {
      setLoading(true);
      try {
        const s = await fetchOfficeServiceById(serviceId);
        if (s) {
          setFormData({
            serviceName: s.Service_Name ?? s.service_name ?? "",
            serviceCategory: s.Service_Category ?? s.service_category ?? "",
            contactName: s.Contact_Name ?? s.contact_name ?? "",
            contactPhone: s.Contact_Phone ?? s.contact_phone ?? "",
            altPhone: s.Alt_Phone ?? s.alt_phone ?? "",
            supportEmail: s.Support_Email ?? s.support_email ?? "",
            supportHours: s.Support_Hours ?? s.support_hours ?? "",
            supportDays: s.Support_Days ?? s.support_days ?? "",
            notes: s.Notes ?? s.notes ?? "",
            status: s.Status ?? s.status ?? "Active",
            tollFreeNumber: s.Toll_Free_Number ?? s.toll_free_number ?? ""
          });
        } else {
          alert("Service not found.");
        }
      } catch (err) {
        console.error("Error fetching office service:", err);
        alert("Failed to load service.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [serviceId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.serviceName?.trim()) {
      setError("Service Name is required.");
      return;
    }
    if (!formData.serviceCategory?.trim()) {
      setError("Service Category is required.");
      return;
    }

    const payload = {
      Service_Name: formData.serviceName.trim(),
      Service_Category: formData.serviceCategory.trim(),
      Contact_Name: formData.contactName.trim() || null,
      Contact_Phone: formData.contactPhone.trim() || null,
      Alt_Phone: formData.altPhone.trim() || null,
      Support_Email: formData.supportEmail.trim() || null,
      Support_Hours: formData.supportHours.trim() || null,
      Support_Days: formData.supportDays.trim() || null,
      Notes: formData.notes.trim() || null,
      Status: formData.status || "Active",
      Toll_Free_Number: formData.tollFreeNumber.trim() || null
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        payload.Updated_By = getUserId();
        await updateOfficeService(serviceId, payload);
        alert("Office service updated successfully.");
      } else {
        payload.Created_By = getUserId();
        await createOfficeService(payload);
        alert("Office service created successfully.");
      }
      if (onAddService) onAddService();
      if (onBack) onBack();
      else navigate("/support-contacts/service");
    } catch (err) {
      console.error("Error creating office service:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to create office service.";
      setError(msg);
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addsupportcontact-container">
      <div className="addsupportcontact-header">
        <div className="addsupportcontact-title-section">
          <button
            type="button"
            className="addsupportcontact-back-link"
            onClick={() => (onBack ? onBack() : navigate("/support-contacts/service"))}
          >
            <FaArrowLeft /> Back to Support Service
          </button>
        </div>
      </div>

      <div className="addsupportcontact-form-card">
        {loading ? (
          <div className="addsupportcontact-loading">Loading service...</div>
        ) : (
          <form className="addsupportcontact-form" onSubmit={handleSubmit}>
          {error && (
            <div className="addsupportcontact-error" role="alert">
              {error}
            </div>
          )}
          <div className="addofficeservice-form-grid">
            <div className="addsupportcontact-form-group">
              <label>
                Service Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleInputChange}
      
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>
                Service Category <span className="required">*</span>
              </label>
              <input
                type="text"
                name="serviceCategory"
                value={formData.serviceCategory}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Contact Name</label>
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleInputChange}
               
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Contact Phone</label>
              <input
                type="text"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Alt Phone</label>
              <input
                type="text"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleInputChange}
                
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Toll Free</label>
              <input
                type="text"
                name="tollFreeNumber"
                value={formData.tollFreeNumber}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Support Email</label>
              <input
                type="email"
                name="supportEmail"
                value={formData.supportEmail}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Support Hours</label>
              <input
                type="text"
                name="supportHours"
                value={formData.supportHours}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Support Days</label>
              <input
                type="text"
                name="supportDays"
                value={formData.supportDays}
                onChange={handleInputChange}
            
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="addsupportcontact-form-group addofficeservice-notes-span">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
      
              />
            </div>
          </div>
          <div className="addsupportcontact-form-actions">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Office Service" : "Create Office Service")}
            </button>
            {!isEdit ? (
              <button
                type="button"
                className="btn-cancel"
                onClick={() => (onBack ? onBack() : navigate("/support-contacts/service"))}
                disabled={submitting}
              >
                Cancel
              </button>
            ) : null}
          </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default AddOfficeService;
